import { HashIcon, LockSimpleIcon, PlusCircleIcon } from '@phosphor-icons/react';
import type { Editor, Range } from '@tiptap/core';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';

import {
  COMPOSER_OVERLAY_CONTENT_CLASS,
  COMPOSER_OVERLAY_SCROLL_BODY_CLASS,
} from '@/components/create/v2/composer/composer-overlay-contract';
import { PlatformBrandIcon } from '@/components/icons/platform-brand-icon';
import { useSettingsDialog } from '@/components/settings/SettingsDialogProvider';
import { identityColorVar } from '@/components/ui/identity-color';
import type { SlackMentionResult } from '@/lib/slack/fetch-slack-mentions';
import { cn } from '@/lib/utils';

/**
 * Small colored initial dot — same per-person hue everywhere that name
 * appears (the chip inserted into the message uses the same function).
 * Foreground is `--identity-badge-foreground` (near-black), NOT white —
 * measured, not eyeballed: white text on these pastel identity hues is
 * 1.9–2.7:1 contrast (fails WCAG AA's 4.5:1 floor); near-black measures
 * 7.9–11.1:1 against the same four hues. See the token comment in
 * `src/index.css` for the full measurement.
 *
 * Carries a small Slack corner badge (platform-badge-on-avatar, the same
 * composition messaging apps use) — founder feedback, live: with only one
 * `@`-mention source in the app today there's no live ambiguity, but the
 * badge costs nothing now and prevents a real one the day Mora gets its own
 * internal @-teammate tagging — two visually-identical mention chips from
 * different sources would be a genuine "who is this actually" problem.
 */
function IdentityDot({ name }: { name: string }) {
  const colorVar = identityColorVar(name);
  return (
    <span className="relative inline-flex size-5 shrink-0" aria-hidden>
      <span
        className="flex size-5 items-center justify-center rounded-full text-micro font-medium"
        style={{
          backgroundColor: `hsl(var(${colorVar}))`,
          color: 'hsl(var(--identity-badge-foreground))',
        }}
      >
        {name.trim().charAt(0).toUpperCase() || '?'}
      </span>
      <span className="absolute -right-0.5 -bottom-0.5 flex size-2.5 items-center justify-center rounded-full bg-popover">
        <PlatformBrandIcon platform="slack" className="size-2" />
      </span>
    </span>
  );
}

export interface SlackMentionListHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface SlackMentionListProps {
  items: SlackMentionResult[];
  loading?: boolean;
  command: (attrs: { id: string; label: string }) => void;
  /** Provided by tiptap's Suggestion plugin alongside `command` (both are
   *  part of the same `SuggestionProps` object `slack-mention-suggestion.tsx`
   *  forwards wholesale) — used only for the channel branch below, which
   *  inserts plain `#name` text instead of an `@`-mention node. */
  editor?: Editor;
  range?: Range;
  /**
   * `overlay` (default): the composer's floating popup, with its own popover chrome.
   * `inline`: the same rows inside a host that already provides the chrome (the editorial
   * note Tag picker's Popover), so the list does not draw a second card inside the first.
   */
  variant?: 'overlay' | 'inline';
}

/**
 * The `@`-mention dropdown for the Home composer's `Mention` extension
 * (`slack-mention-suggestion.tsx`). Renders NOTHING (not even an empty-state
 * row) only when there are zero items AND nothing is loading — most `@`
 * keystrokes in normal writing are NOT a Slack mention (an email address, a
 * plain "at" in prose), and this composer's input row is a DO-NOT-CROWD zone
 * (root CLAUDE.md) — a popup that appears for every bare `@` would be
 * exactly the kind of clutter that rule exists to prevent. This is still
 * true when Slack has no connection: `fetchSlackMentions` returns a single
 * `connect-prompt` item in that case (never a permanent chip on the row
 * itself — only inside a popup the user explicitly opened by typing `@`),
 * rendered below as a CTA instead of a member row.
 */
export const SlackMentionList = forwardRef<SlackMentionListHandle, SlackMentionListProps>(
  ({ items, loading, command, editor, range, variant = 'overlay' }, ref) => {
    const chrome = variant === 'overlay' ? COMPOSER_OVERLAY_CONTENT_CLASS : undefined;
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { openSettings } = useSettingsDialog();

    useEffect(() => {
      setSelectedIndex(0);
    }, [items]);

    const selectItem = (index: number) => {
      const item = items[index];
      if (!item) return;
      if (item.kind === 'connect-prompt') {
        openSettings('integrations');
        return;
      }
      if (item.kind === 'channel') {
        // Plain-text `#name` rather than `command({id, label})` — the
        // Mention node's renderText always prefixes `@`, which would read as
        // "DM a channel," and `send_to_slack`'s own `channelName` docs
        // already model this exact `#channel-name` shape. Falls back to the
        // mention-node path only if tiptap somehow didn't hand this
        // component an editor/range (defensive; always present in practice —
        // see the props doc comment).
        if (editor && range) {
          editor.chain().focus().deleteRange(range).insertContent(`#${item.name} `).run();
          return;
        }
        command({ id: item.id, label: item.name });
        return;
      }
      command({ id: item.id, label: item.name });
    };

    useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }) => {
        if (items.length === 0) return false;
        if (event.key === 'ArrowUp') {
          setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
          return true;
        }
        if (event.key === 'ArrowDown') {
          setSelectedIndex((prev) => (prev + 1) % items.length);
          return true;
        }
        if (event.key === 'Enter' || event.key === 'Tab') {
          selectItem(selectedIndex);
          return true;
        }
        return false;
      },
    }));

    if (loading) {
      return (
        <div
          className={cn(
            chrome,
            'flex items-center gap-2 px-3 py-2 text-caption text-muted-foreground',
          )}
        >
          <PlatformBrandIcon platform="slack" className="size-3.5" />
          Searching Slack…
        </div>
      );
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <div className={cn(chrome, variant === 'overlay' ? 'w-56' : 'w-full', 'overflow-hidden p-1')}>
        <div className={cn(COMPOSER_OVERLAY_SCROLL_BODY_CLASS, 'max-h-64')}>
          {items.map((item, index) => (
            <button
              key={item.kind === 'connect-prompt' ? 'connect-prompt' : item.id}
              type="button"
              className={cn(
                'flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-caption transition-colors',
                index === selectedIndex
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/60',
              )}
              onMouseDown={(event) => {
                // mousedown (not click) fires before the editor blurs, so the
                // ProseMirror suggestion range is still valid when `command`
                // runs. preventDefault stops that blur from happening at all.
                event.preventDefault();
                selectItem(index);
              }}
            >
              {item.kind === 'connect-prompt' ? (
                <>
                  <PlusCircleIcon className="size-4 shrink-0" weight="regular" />
                  <span className="truncate">Connect Slack to mention teammates</span>
                </>
              ) : item.kind === 'channel' ? (
                <>
                  {item.isPrivate ? (
                    <LockSimpleIcon className="size-4 shrink-0" weight="regular" />
                  ) : (
                    <HashIcon className="size-4 shrink-0" weight="regular" />
                  )}
                  <span className="truncate">{item.name}</span>
                </>
              ) : (
                <>
                  <IdentityDot name={item.name} />
                  <span className="truncate">{item.name}</span>
                </>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  },
);
SlackMentionList.displayName = 'SlackMentionList';
